
/**
 * Utility to parse chapter timestamps from text or TipTap JSON.
 * Expected format: "00:00:10 - Title" or "00:10 - Title"
 */

interface Chapter {
  start: number;
  title: string;
}

interface ChapterWithIndex extends Chapter {
  index: number;
}

interface TipTapNode {
  type?: string;
  text?: string;
  content?: TipTapNode[];
}

interface TipTapContent {
  json?: string | { content?: TipTapNode[] };
  content?: TipTapNode[];
}

export const parseChapters = (overview: string | TipTapContent | null | undefined): Chapter[] => {
    if (!overview) return [];

    let textLines: string[] = [];

    // Helper to extract text lines
    const getLines = (content: string | TipTapContent | null | undefined): string[] => {
        if (!content) return [];
        if (typeof content === 'string') return content.split('\n');
        
        // Handle TipTap JSON structure
        if (typeof content === 'object') {
            // Traverse content to find paragraphs/blocks
            const lines: string[] = [];
            
            const extractText = (nodes: TipTapNode[] | undefined): string => {
                if (!Array.isArray(nodes)) return '';
                return nodes.map(node => {
                    if (node.type === 'text' && node.text) return node.text;
                    return '';
                }).join('');
            };

            const traverse = (nodes: TipTapNode[] | undefined): void => {
                if (!Array.isArray(nodes)) return;
                nodes.forEach(node => {
                    if (node.type === 'paragraph' || node.type === 'heading') {
                        const text = extractText(node.content);
                        if (text.trim()) lines.push(text);
                    } else if (node.content) {
                        traverse(node.content);
                    }
                });
            };

            const rootContent = (content as TipTapContent).json 
                ? (typeof (content as TipTapContent).json === 'string' ? JSON.parse((content as TipTapContent).json as string) : (content as TipTapContent).json) 
                : content;
                
            if (rootContent && typeof rootContent === 'object' && 'content' in rootContent) {
                traverse((rootContent as TipTapContent).content);
            }
            
            return lines;
        }
        return [];
    };

    textLines = getLines(overview);

    const chapters: Chapter[] = [];
    // Regex for: HH:MM:SS - Title OR MM:SS - Title
    // Supports:
    // 00:00:10 - Intro
    // 05:30 - Topic
    // 10:20 - Another Topic
    const timestampRegex = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\s*-\s*(.+)$/;

    textLines.forEach(line => {
        const cleanLine = line.trim();
        const match = cleanLine.match(timestampRegex);
        if (match) {
            const hours = match[1] ? parseInt(match[1], 10) : 0;
            const minutes = parseInt(match[2], 10);
            const seconds = parseInt(match[3], 10);
            const title = match[4].trim();
            
            const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
            chapters.push({ start: totalSeconds, title });
        }
    });

    return chapters.sort((a, b) => a.start - b.start);
};

export const getCurrentChapter = (chapters: Chapter[] | null | undefined, currentTime: number): ChapterWithIndex | null => {
    if (!chapters || chapters.length === 0) return null;
    
    // Find the last chapter that started before or at currentTime
    for (let i = chapters.length - 1; i >= 0; i--) {
        if (currentTime >= chapters[i].start) {
            return { ...chapters[i], index: i + 1 };
        }
    }
    
    // If before first chapter
    return null; 
};

